import React, { useState, useContext, useEffect, useMemo, useCallback } from "react";
import clsx from "clsx";
import {
  makeStyles,
  Drawer,
  AppBar,
  Button,
  Toolbar,
  List,
  Typography,
  Divider,
  MenuItem,
  IconButton,
  Menu,
  Switch,
  Tooltip,
} from "@material-ui/core";
import MenuIcon from "@material-ui/icons/Menu";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import AccountCircle from "@material-ui/icons/AccountCircle";
import Brightness4Icon from "@material-ui/icons/Brightness4";
import TranslateIcon from "@material-ui/icons/Translate";
import VolumeOffIcon from "@material-ui/icons/VolumeOff";
import VolumeUpIcon from "@material-ui/icons/VolumeUp";
import ReportProblemIcon from "@material-ui/icons/ReportProblem";
import CloseIcon from "@material-ui/icons/Close";
import { useHistory } from "react-router-dom";

import MainListItems from "./MainListItems";
import NotificationsPopOver from "../components/NotificationsPopOver";
import UserModal from "../components/UserModal";
import { AuthContext } from "../context/Auth/AuthContext";
import { WhatsAppsContext } from "../context/WhatsApp/WhatsAppsContext";
import BackdropLoading from "../components/BackdropLoading";
import { i18n } from "../translate/i18n";
import { useThemeContext } from "../context/DarkMode";
import Avatar from "@material-ui/core/Avatar";
import SettingsOutlinedIcon from "@material-ui/icons/SettingsOutlined";
import brandBrain from "../assets/branding/brand-brain-mark.png";
import {
  ensureNotificationSoundReady,
  isNotificationSoundMuted,
  playNotificationSound,
  setNotificationSoundMuted,
  subscribeNotificationSoundMuted
} from "../services/notificationSound";
import api from "../services/api";

const drawerWidth = 240;
const supportedLanguages = ["pt", "en", "es"];
const connectionAlertDismissKey = "altchat:connection-alert-dismissed-signature";
const globalAnnouncementDismissKey = "altchat:global-announcement-dismissed-signature";
const globalAnnouncementUpdatedEvent = "altchat:global-announcement-updated";

const normalizeLanguage = language => {
  const normalized = String(language || "pt").toLowerCase().slice(0, 2);
  return supportedLanguages.includes(normalized) ? normalized : "pt";
};

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    height: "100vh",
  },
  toolbar: {
    paddingRight: 24,
    minHeight: 70,
    paddingLeft: 18,
    [theme.breakpoints.down("sm")]: {
      paddingRight: 10,
      paddingLeft: 10,
      minHeight: 64,
    },
  },
  toolbarIcon: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    padding: "12px 12px",
    minHeight: "72px",
    "& button": {
      color: "#cddde3",
      "&:hover": {
        color: "#ffffff",
        backgroundColor: "rgba(255, 255, 255, 0.08)",
      },
    },
  },
  brandHeader: {
    display: "flex",
    alignItems: "center",
    gap: 11,
    padding: "16px 14px 14px",
    minHeight: 70,
  },
  brandHeaderCompact: {
    justifyContent: "center",
    padding: "16px 0 14px",
  },
  brandMark: {
    width: 36,
    height: 36,
    flexShrink: 0,
    borderRadius: 10,
    background: "linear-gradient(135deg, #14b8a6 0%, #0f766e 100%)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: "0 6px 14px -4px rgba(13,148,136,0.55)",
    "& img": {
      width: 24,
      height: 24,
      objectFit: "contain",
    },
  },
  brandName: {
    flex: 1,
    minWidth: 0,
    color: "#ffffff",
    fontWeight: 800,
    fontSize: 18,
    letterSpacing: "-0.02em",
    lineHeight: 1,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    "& b": {
      color: "#2dd4bf",
      fontWeight: 800,
    },
  },
  collapseBtn: {
    flexShrink: 0,
    width: 30,
    height: 30,
    padding: 0,
    borderRadius: 8,
    color: "#7e96a2",
    backgroundColor: "#11212b",
    "&:hover": {
      backgroundColor: "#1b3340",
      color: "#cfe7ee",
    },
  },
  navScroll: {
    flex: 1,
    minHeight: 0,
    overflowY: "auto",
    overflowX: "hidden",
  },
  sideFoot: {
    padding: "10px 12px 14px",
    borderTop: "1px solid rgba(255,255,255,0.06)",
  },
  userCard: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: 8,
    borderRadius: 12,
    backgroundColor: "#0a1620",
    cursor: "pointer",
    transition: "background-color 0.15s ease",
    "&:hover": {
      backgroundColor: "#11212b",
    },
  },
  userCardCompact: {
    justifyContent: "center",
  },
  userAvatarWrap: {
    position: "relative",
    flexShrink: 0,
  },
  userAvatar: {
    width: 36,
    height: 36,
    fontSize: 14,
    fontWeight: 700,
    color: "#fff",
    background: "linear-gradient(135deg, #3aa08e 0%, #1f6f5c 100%)",
  },
  userStatus: {
    position: "absolute",
    right: -1,
    bottom: -1,
    width: 11,
    height: 11,
    borderRadius: "50%",
    border: "2.5px solid #0a1620",
    backgroundColor: "#30d158",
  },
  userInfo: {
    flex: 1,
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    gap: 2,
  },
  userName: {
    display: "block",
    color: "#ffffff",
    fontSize: 13,
    fontWeight: 600,
    lineHeight: 1.2,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  userRole: {
    display: "block",
    color: "#6f8794",
    fontSize: 11,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  userGear: {
    color: "#5e7784",
    fontSize: 20,
    flexShrink: 0,
  },
  appBar: {
    zIndex: theme.zIndex.drawer + 1,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    backgroundColor: "transparent",
    borderBottom: "none",
    backdropFilter: "blur(14px)",
  },
  appBarShift: {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  },
  menuButton: {
    marginRight: 18,
    color: theme.palette.text.primary,
    backgroundColor:
      theme.palette.type === "dark"
        ? "rgba(148, 163, 184, 0.10)"
        : "rgba(255, 255, 255, 0.85)",
    borderRadius: 16,
    border: `1px solid ${
      theme.palette.type === "dark"
        ? "rgba(71, 85, 105, 0.24)"
        : "rgba(148, 163, 184, 0.18)"
    }`,
    boxShadow:
      theme.palette.type === "dark"
        ? "0 12px 24px rgba(2, 6, 23, 0.20)"
        : "0 10px 24px rgba(15, 23, 42, 0.06)",
    [theme.breakpoints.down("sm")]: {
      marginRight: 10,
    },
  },
  menuButtonHidden: {
    display: "none",
  },
  title: {
    flexGrow: 1,
    display: "flex",
    alignItems: "center",
    minHeight: 46,
  },
  brandWrap: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    minWidth: 0,
    [theme.breakpoints.down("sm")]: {
      gap: 8,
    },
  },
  brandIcon: {
    width: 40,
    height: 40,
    objectFit: "contain",
    borderRadius: 0,
    filter:
      theme.palette.type === "dark"
        ? "drop-shadow(0 9px 16px rgba(2, 6, 23, 0.20))"
        : "drop-shadow(0 8px 14px rgba(15, 23, 42, 0.08))",
    [theme.breakpoints.down("sm")]: {
      width: 32,
      height: 32,
    },
  },
  brandText: {
    color: theme.palette.text.primary,
    fontWeight: 800,
    letterSpacing: "-0.02em",
    fontSize: 18,
    lineHeight: 1,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    [theme.breakpoints.down("sm")]: {
      fontSize: 16,
      maxWidth: 110,
    },
  },
  drawerPaper: {
    position: "relative",
    whiteSpace: "nowrap",
    width: drawerWidth,
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    background: "linear-gradient(180deg, #0c1a23 0%, #0a151c 100%)",
    borderRight: "none",
    display: "flex",
    flexDirection: "column",
    height: "100%",
  },
  drawerPaperClose: {
    overflowX: "hidden",
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    width: theme.spacing(7),
    [theme.breakpoints.up("sm")]: {
      width: theme.spacing(9),
    },
  },
  appBarSpacer: {
    minHeight: "82px",
    [theme.breakpoints.down("sm")]: {
      minHeight: "72px",
    },
  },
  content: {
    flex: 1,
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    background:
      theme.palette.type === "dark"
        ? "linear-gradient(180deg, rgba(12,18,25,0.98) 0%, rgba(15,23,32,0.98) 100%)"
        : "linear-gradient(180deg, rgba(246,248,250,0.98) 0%, rgba(241,245,248,0.98) 100%)",
  },
  contentBody: {
    flex: 1,
    minHeight: 0,
    overflowY: "auto",
    overflowX: "hidden",
  },
  container: {
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4),
  },
  paper: {
    padding: theme.spacing(2),
    display: "flex",
    overflow: "auto",
    flexDirection: "column",
  },
  switch: {
    transform: "scale(0.8)",
  },
  iconButton: {
    color: theme.palette.text.primary,
    backgroundColor:
      theme.palette.type === "dark"
        ? "rgba(148, 163, 184, 0.10)"
        : "rgba(255, 255, 255, 0.85)",
    borderRadius: 16,
    marginLeft: 8,
    border: `1px solid ${
      theme.palette.type === "dark"
        ? "rgba(71, 85, 105, 0.24)"
        : "rgba(148, 163, 184, 0.18)"
    }`,
    boxShadow:
      theme.palette.type === "dark"
        ? "0 12px 24px rgba(2, 6, 23, 0.18)"
        : "0 10px 24px rgba(15, 23, 42, 0.05)",
    [theme.breakpoints.down("sm")]: {
      marginLeft: 6,
    },
  },
  languageButton: {
    minWidth: 58,
    gap: 6,
    fontSize: 12,
    fontWeight: 800,
    textTransform: "uppercase",
  },
  themeSwitchContainer: {
    display: "flex",
    alignItems: "center",
    padding: "6px 10px",
    borderRadius: 999,
    backgroundColor:
      theme.palette.type === "dark"
        ? "rgba(148, 163, 184, 0.10)"
        : "rgba(255, 255, 255, 0.88)",
    border: `1px solid ${
      theme.palette.type === "dark"
        ? "rgba(71, 85, 105, 0.24)"
        : "rgba(148, 163, 184, 0.16)"
    }`,
    boxShadow:
      theme.palette.type === "dark"
        ? "0 12px 24px rgba(2, 6, 23, 0.16)"
        : "0 10px 24px rgba(15, 23, 42, 0.05)",
    [theme.breakpoints.down("sm")]: {
      padding: "4px 6px",
      marginLeft: "auto",
    },
  },
  themeIcon: {
    color: theme.palette.text.primary,
  },
  supportBanner: {
    backgroundColor:
      theme.palette.type === "dark"
        ? "rgba(17, 57, 52, 0.72)"
        : "#f6fbf9",
    color: theme.palette.type === "dark" ? "#d8f5ee" : "#0c5f55",
    border: `1px solid ${
      theme.palette.type === "dark"
        ? "rgba(67, 156, 142, 0.28)"
        : "rgba(18, 140, 126, 0.14)"
    }`,
    padding: theme.spacing(1.75, 2.5),
    margin: theme.spacing(1.5, 2, 0),
    borderRadius: 20,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(2),
    boxShadow:
      theme.palette.type === "dark"
        ? "0 16px 30px rgba(2, 6, 23, 0.16)"
        : "0 16px 30px rgba(18, 140, 126, 0.06)",
    [theme.breakpoints.down("sm")]: {
      margin: theme.spacing(1, 1, 0),
      padding: theme.spacing(1.5),
      alignItems: "flex-start",
      flexDirection: "column",
    },
  },
  supportBannerText: {
    flex: 1,
    lineHeight: 1.5,
    paddingRight: theme.spacing(1),
  },
  connectionAlert: {
    margin: theme.spacing(1.5, 2, 0),
    padding: theme.spacing(1.75, 2),
    borderRadius: 18,
    border: `1px solid ${
      theme.palette.type === "dark"
        ? "rgba(245, 158, 11, 0.34)"
        : "rgba(245, 158, 11, 0.26)"
    }`,
    background:
      theme.palette.type === "dark"
        ? "linear-gradient(135deg, rgba(120, 53, 15, 0.40), rgba(30, 41, 59, 0.88))"
        : "linear-gradient(135deg, rgba(255, 251, 235, 0.98), rgba(255, 255, 255, 0.96))",
    color: theme.palette.type === "dark" ? "#fdecc8" : "#78350f",
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: theme.spacing(2),
    boxShadow:
      theme.palette.type === "dark"
        ? "0 18px 34px rgba(2, 6, 23, 0.26)"
        : "0 18px 34px rgba(146, 64, 14, 0.10)",
    [theme.breakpoints.down("sm")]: {
      margin: theme.spacing(1, 1, 0),
      flexDirection: "column",
    },
  },
  connectionAlertContent: {
    display: "flex",
    alignItems: "flex-start",
    gap: theme.spacing(1.5),
    minWidth: 0,
  },
  connectionAlertIcon: {
    color: "#d97706",
    marginTop: 2,
    flexShrink: 0,
  },
  connectionAlertTitle: {
    fontWeight: 800,
    lineHeight: 1.25,
  },
  connectionAlertDescription: {
    color: theme.palette.type === "dark" ? "#f8dfb0" : "#92400e",
    marginTop: theme.spacing(0.25),
    lineHeight: 1.45,
  },
  connectionAlertActions: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.75),
    flexShrink: 0,
    [theme.breakpoints.down("sm")]: {
      width: "100%",
      justifyContent: "space-between",
    },
  },
  connectionAlertButton: {
    borderRadius: 999,
    fontWeight: 800,
    borderColor: theme.palette.type === "dark" ? "#f59e0b" : "#d97706",
    color: theme.palette.type === "dark" ? "#fef3c7" : "#92400e",
  },
  globalAnnouncement: {
    margin: theme.spacing(1.5, 2, 0),
    padding: theme.spacing(1.75, 2),
    borderRadius: 18,
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: theme.spacing(2),
    border: "1px solid rgba(56, 189, 248, 0.30)",
    background:
      theme.palette.type === "dark"
        ? "linear-gradient(135deg, rgba(8, 47, 73, 0.58), rgba(30, 41, 59, 0.88))"
        : "linear-gradient(135deg, rgba(239, 246, 255, 0.98), rgba(255, 255, 255, 0.96))",
    color: theme.palette.type === "dark" ? "#dbeafe" : "#0c4a6e",
    boxShadow:
      theme.palette.type === "dark"
        ? "0 18px 34px rgba(2, 6, 23, 0.26)"
        : "0 18px 34px rgba(14, 116, 144, 0.08)",
    [theme.breakpoints.down("sm")]: {
      margin: theme.spacing(1, 1, 0),
      flexDirection: "column",
    },
  },
  globalAnnouncementWarning: {
    borderColor: theme.palette.type === "dark" ? "rgba(245, 158, 11, 0.34)" : "rgba(245, 158, 11, 0.30)",
    background:
      theme.palette.type === "dark"
        ? "linear-gradient(135deg, rgba(120, 53, 15, 0.40), rgba(30, 41, 59, 0.88))"
        : "linear-gradient(135deg, rgba(255, 251, 235, 0.98), rgba(255, 255, 255, 0.96))",
    color: theme.palette.type === "dark" ? "#fdecc8" : "#78350f",
  },
  globalAnnouncementCritical: {
    borderColor: theme.palette.type === "dark" ? "rgba(248, 113, 113, 0.34)" : "rgba(239, 68, 68, 0.30)",
    background:
      theme.palette.type === "dark"
        ? "linear-gradient(135deg, rgba(127, 29, 29, 0.44), rgba(30, 41, 59, 0.88))"
        : "linear-gradient(135deg, rgba(254, 242, 242, 0.98), rgba(255, 255, 255, 0.96))",
    color: theme.palette.type === "dark" ? "#fecaca" : "#7f1d1d",
  },
  globalAnnouncementContent: {
    display: "flex",
    alignItems: "flex-start",
    gap: theme.spacing(1.5),
    minWidth: 0,
  },
  globalAnnouncementIcon: {
    marginTop: 2,
    flexShrink: 0,
  },
  globalAnnouncementTitle: {
    fontWeight: 800,
    lineHeight: 1.25,
  },
  globalAnnouncementMessage: {
    marginTop: theme.spacing(0.25),
    lineHeight: 1.45,
    whiteSpace: "pre-line",
  },
  globalAnnouncementActions: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.75),
    flexShrink: 0,
    [theme.breakpoints.down("sm")]: {
      width: "100%",
      justifyContent: "flex-end",
    },
  }
}));

const LoggedInLayout = ({ children }) => {
  const classes = useStyles();
  const history = useHistory();
  const [userModalOpen, setUserModalOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [languageAnchorEl, setLanguageAnchorEl] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const { handleLogout, loading, stopSupportSession } = useContext(AuthContext);
  const { whatsApps = [] } = useContext(WhatsAppsContext);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerVariant, setDrawerVariant] = useState("permanent");
  const [dismissedConnectionAlertSignature, setDismissedConnectionAlertSignature] = useState(
    () => window.localStorage.getItem(connectionAlertDismissKey) || ""
  );
  const [dismissedGlobalAnnouncementSignature, setDismissedGlobalAnnouncementSignature] = useState(
    () => window.localStorage.getItem(globalAnnouncementDismissKey) || ""
  );
  const [globalAnnouncement, setGlobalAnnouncement] = useState({
    active: false,
    announcement: null
  });
  const [notificationSoundsMuted, setNotificationSoundsMutedState] = useState(
    () => isNotificationSoundMuted()
  );
  const { user } = useContext(AuthContext);
  const userDisplayName = String(
    user?.name || user?.company?.name || "Usuário"
  ).trim();
  const userInitials = userDisplayName
    .split(/\s+/)
    .map(part => part[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();
  const userRoleLabel = useMemo(() => {
    switch (String(user?.profile || "").toLowerCase()) {
      case "superadmin":
        return "Super admin";
      case "admin":
        return "Supervisor";
      default:
        return "Atendente";
    }
  }, [user?.profile]);
  const { darkMode, toggleTheme } = useThemeContext();
  const [currentLanguage, setCurrentLanguage] = useState(() =>
    normalizeLanguage(i18n.language || window.localStorage.getItem("i18nextLng"))
  );

  useEffect(() => {
    ensureNotificationSoundReady();
    return subscribeNotificationSoundMuted(setNotificationSoundsMutedState);
  }, []);

  useEffect(() => {
    const handleLanguageChanged = language => {
      setCurrentLanguage(normalizeLanguage(language));
    };

    i18n.on("languageChanged", handleLanguageChanged);
    return () => i18n.off("languageChanged", handleLanguageChanged);
  }, []);

  useEffect(() => {
    if (document.body.offsetWidth < 600) {
      setDrawerVariant("temporary");
    } else {
      setDrawerVariant("permanent");
    }
  }, [drawerOpen]);

  const connectionAlertItems = useMemo(() => {
    const problemStatuses = ["DISCONNECTED", "TIMEOUT", "PAIRING", "QRCODE", "OPENING"];

    return (Array.isArray(whatsApps) ? whatsApps : []).filter(whatsApp => {
      if (!whatsApp || whatsApp.archivedAt) return false;

      const connectionType = String(whatsApp.connectionType || "baileys").toLowerCase();
      const status = String(whatsApp.status || "").toUpperCase();

      return ["baileys", "baileys_official"].includes(connectionType) &&
        problemStatuses.includes(status);
    });
  }, [whatsApps]);

  const connectionAlertSignature = useMemo(
    () =>
      connectionAlertItems
        .map(whatsApp => `${whatsApp.id}:${String(whatsApp.status || "").toUpperCase()}`)
        .sort()
        .join("|"),
    [connectionAlertItems]
  );

  const shouldShowConnectionAlert =
    user?.profile !== "superadmin" &&
    connectionAlertItems.length > 0 &&
    connectionAlertSignature !== dismissedConnectionAlertSignature;

  const loadGlobalAnnouncement = useCallback(async () => {
    if (loading || !user?.id) {
      return;
    }

    try {
      const { data } = await api.get("/global-announcement/active");
      setGlobalAnnouncement({
        active: Boolean(data?.active),
        announcement: data?.announcement || null
      });
    } catch (error) {
      setGlobalAnnouncement({ active: false, announcement: null });
    }
  }, [loading, user?.id]);

  useEffect(() => {
    if (loading || !user?.id) {
      return undefined;
    }

    loadGlobalAnnouncement();
    const interval = setInterval(loadGlobalAnnouncement, 60000);
    return () => clearInterval(interval);
  }, [loadGlobalAnnouncement, loading, user?.id]);

  useEffect(() => {
    const handleGlobalAnnouncementUpdated = () => {
      loadGlobalAnnouncement();
    };

    window.addEventListener(
      globalAnnouncementUpdatedEvent,
      handleGlobalAnnouncementUpdated
    );

    return () => {
      window.removeEventListener(
        globalAnnouncementUpdatedEvent,
        handleGlobalAnnouncementUpdated
      );
    };
  }, [loadGlobalAnnouncement]);

  const globalAnnouncementSignature = useMemo(() => {
    if (!globalAnnouncement?.announcement) return "";
    const payload = globalAnnouncement.announcement;
    return [
      payload.title || "",
      payload.message || "",
      payload.severity || "warning",
      payload.startsAt || "",
      payload.endsAt || "",
      payload.updatedAt || ""
    ].join("|");
  }, [globalAnnouncement]);

  const shouldShowGlobalAnnouncement = useMemo(() => {
    const payload = globalAnnouncement?.announcement;
    if (!globalAnnouncement?.active || !payload?.message) return false;
    if (!payload.dismissible) return true;
    return globalAnnouncementSignature !== dismissedGlobalAnnouncementSignature;
  }, [globalAnnouncement, globalAnnouncementSignature, dismissedGlobalAnnouncementSignature]);

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
    setMenuOpen(true);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setMenuOpen(false);
  };

  const handleOpenLanguageMenu = event => {
    setLanguageAnchorEl(event.currentTarget);
  };

  const handleCloseLanguageMenu = () => {
    setLanguageAnchorEl(null);
  };

  const handleChangeLanguage = language => {
    const nextLanguage = normalizeLanguage(language);
    window.localStorage.setItem("i18nextLng", nextLanguage);
    i18n.changeLanguage(nextLanguage);
    setCurrentLanguage(nextLanguage);
    handleCloseLanguageMenu();
  };

  const handleOpenUserModal = () => {
    setUserModalOpen(true);
    handleCloseMenu();
  };

  const handleClickLogout = () => {
    handleCloseMenu();
    handleLogout();
  };

  const handleToggleNotificationSound = () => {
    const nextMuted = !notificationSoundsMuted;
    setNotificationSoundMuted(nextMuted);

    if (!nextMuted) {
      void playNotificationSound("message");
    }
  };

  const handleDismissConnectionAlert = () => {
    window.localStorage.setItem(connectionAlertDismissKey, connectionAlertSignature);
    setDismissedConnectionAlertSignature(connectionAlertSignature);
  };

  const handleDismissGlobalAnnouncement = () => {
    window.localStorage.setItem(
      globalAnnouncementDismissKey,
      globalAnnouncementSignature
    );
    setDismissedGlobalAnnouncementSignature(globalAnnouncementSignature);
  };

  const handleViewConnections = () => {
    history.push("/connections");
  };

  const drawerClose = () => {
    if (document.body.offsetWidth < 600) {
      setDrawerOpen(false);
    }
  };

  if (loading) {
    return <BackdropLoading />;
  }

  return (
    <div className={classes.root}>
      <Drawer
        variant={drawerVariant}
        className={drawerOpen ? classes.drawerPaper : classes.drawerPaperClose}
        classes={{
          paper: clsx(
            classes.drawerPaper,
            !drawerOpen && classes.drawerPaperClose
          ),
        }}
        open={drawerOpen}
      >
        <div
          className={clsx(classes.brandHeader, {
            [classes.brandHeaderCompact]: !drawerOpen,
          })}
        >
          <span className={classes.brandMark}>
            <img src={brandBrain} alt="AltChat" />
          </span>
          {drawerOpen && (
            <>
              <span className={classes.brandName}>
                Alt<b>Chat</b>
              </span>
              <IconButton
                className={classes.collapseBtn}
                onClick={() => setDrawerOpen(false)}
                aria-label="recolher menu"
              >
                <ChevronLeftIcon fontSize="small" />
              </IconButton>
            </>
          )}
        </div>
        <div className={classes.navScroll}>
          <List>
            <MainListItems
              drawerClose={drawerClose}
              onOpenProfile={handleOpenUserModal}
              drawerOpen={drawerOpen}
            />
          </List>
        </div>
        <div className={classes.sideFoot}>
          <Tooltip title={drawerOpen ? "" : userDisplayName} placement="right">
            <div
              className={clsx(classes.userCard, {
                [classes.userCardCompact]: !drawerOpen,
              })}
              onClick={handleOpenUserModal}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  handleOpenUserModal();
                }
              }}
            >
              <span className={classes.userAvatarWrap}>
                <Avatar className={classes.userAvatar}>{userInitials}</Avatar>
                <span className={classes.userStatus} />
              </span>
              {drawerOpen && (
                <>
                  <span className={classes.userInfo}>
                    <span className={classes.userName}>{userDisplayName}</span>
                    <span className={classes.userRole}>
                      {userRoleLabel} · online
                    </span>
                  </span>
                  <SettingsOutlinedIcon className={classes.userGear} />
                </>
              )}
            </div>
          </Tooltip>
        </div>
      </Drawer>
      <UserModal
        open={userModalOpen}
        onClose={() => setUserModalOpen(false)}
        userId={user?.id}
      />
      <AppBar
        position="absolute"
        className={clsx(classes.appBar, drawerOpen && classes.appBarShift)}
      >
        <Toolbar variant="dense" className={classes.toolbar}>
          <IconButton
            edge="start"
            aria-label="open drawer"
            onClick={() => setDrawerOpen(!drawerOpen)}
            className={clsx(
              classes.menuButton,
              drawerOpen && classes.menuButtonHidden
            )}
          >
            <MenuIcon />
          </IconButton>
          <Typography
            component="h1"
            variant="h6"
            noWrap
            className={classes.title}
          >
            <span className={classes.brandWrap}>
              <img
                src={brandBrain}
                alt="AltChat"
                className={classes.brandIcon}
              />
              <span className={classes.brandText}>AltChat</span>
            </span>
          </Typography>

          <div className={classes.themeSwitchContainer}>
            <Brightness4Icon className={classes.themeIcon} />
            <Switch
              checked={darkMode}
              onChange={toggleTheme}
              color="default"
              className={classes.switch}
            />
          </div>

          <Tooltip
            title={
              notificationSoundsMuted
                ? i18n.t("mainDrawer.appBar.sound.enable")
                : i18n.t("mainDrawer.appBar.sound.disable")
            }
          >
            <IconButton
              aria-label={
                notificationSoundsMuted
                  ? i18n.t("mainDrawer.appBar.sound.enable")
                  : i18n.t("mainDrawer.appBar.sound.disable")
              }
              onClick={handleToggleNotificationSound}
              className={classes.iconButton}
            >
              {notificationSoundsMuted ? <VolumeOffIcon /> : <VolumeUpIcon />}
            </IconButton>
          </Tooltip>

          <Tooltip title={i18n.t("mainDrawer.appBar.language.label")}>
            <IconButton
              aria-label={i18n.t("mainDrawer.appBar.language.label")}
              onClick={handleOpenLanguageMenu}
              className={clsx(classes.iconButton, classes.languageButton)}
            >
              <TranslateIcon fontSize="small" />
              <span>{currentLanguage}</span>
            </IconButton>
          </Tooltip>
          <Menu
            id="language-menu"
            anchorEl={languageAnchorEl}
            getContentAnchorEl={null}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "right",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
            open={Boolean(languageAnchorEl)}
            onClose={handleCloseLanguageMenu}
          >
            {supportedLanguages.map(language => (
              <MenuItem
                key={language}
                selected={currentLanguage === language}
                onClick={() => handleChangeLanguage(language)}
              >
                {i18n.t(`mainDrawer.appBar.language.options.${language}`)}
              </MenuItem>
            ))}
          </Menu>

          {user.id &&
            (user.profile !== "superadmin" || user?.supportSession?.active) && (
              <NotificationsPopOver hideButton />
            )}

          <div>
            <IconButton
              aria-label="account of current user"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={handleMenu}
              className={classes.iconButton}
            >
              <AccountCircle />
            </IconButton>
            <Menu
              id="menu-appbar"
              anchorEl={anchorEl}
              getContentAnchorEl={null}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "right",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
              open={menuOpen}
              onClose={handleCloseMenu}
            >
              <MenuItem onClick={handleOpenUserModal}>
                {i18n.t("mainDrawer.appBar.user.profile")}
              </MenuItem>
              <MenuItem onClick={handleClickLogout}>
                {i18n.t("mainDrawer.appBar.user.logout")}
              </MenuItem>
            </Menu>
          </div>
        </Toolbar>
      </AppBar>
      <main className={classes.content}>
        <div className={classes.appBarSpacer} />
        <div key={currentLanguage} className={classes.contentBody}>
        {shouldShowGlobalAnnouncement ? (
          <div
            className={clsx(classes.globalAnnouncement, {
              [classes.globalAnnouncementWarning]:
                (globalAnnouncement?.announcement?.severity || "warning") === "warning",
              [classes.globalAnnouncementCritical]:
                globalAnnouncement?.announcement?.severity === "critical"
            })}
          >
            <div className={classes.globalAnnouncementContent}>
              <ReportProblemIcon className={classes.globalAnnouncementIcon} />
              <div>
                {globalAnnouncement?.announcement?.title ? (
                  <Typography variant="subtitle1" className={classes.globalAnnouncementTitle}>
                    {globalAnnouncement.announcement.title}
                  </Typography>
                ) : null}
                <Typography variant="body2" className={classes.globalAnnouncementMessage}>
                  {globalAnnouncement?.announcement?.message || ""}
                </Typography>
              </div>
            </div>
            {globalAnnouncement?.announcement?.dismissible ? (
              <div className={classes.globalAnnouncementActions}>
                <IconButton
                  size="small"
                  aria-label="Fechar aviso global"
                  onClick={handleDismissGlobalAnnouncement}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </div>
            ) : null}
          </div>
        ) : null}
        {shouldShowConnectionAlert ? (
          <div className={classes.connectionAlert}>
            <div className={classes.connectionAlertContent}>
              <ReportProblemIcon className={classes.connectionAlertIcon} />
              <div>
                <Typography variant="subtitle1" className={classes.connectionAlertTitle}>
                  {i18n.t("connections.extras.disconnectedTitle")}
                </Typography>
                <Typography variant="body2" className={classes.connectionAlertDescription}>
                  {i18n.t("connections.extras.disconnectedDescription")}
                </Typography>
              </div>
            </div>
            <div className={classes.connectionAlertActions}>
              <Button
                size="small"
                variant="outlined"
                className={classes.connectionAlertButton}
                onClick={handleViewConnections}
              >
                {i18n.t("connections.extras.viewConnections")}
              </Button>
              <IconButton
                size="small"
                aria-label="Fechar aviso"
                onClick={handleDismissConnectionAlert}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </div>
          </div>
        ) : null}
        {user?.supportSession?.active ? (
          <div className={classes.supportBanner}>
            <Typography variant="body2" className={classes.supportBannerText}>
              Modo suporte ativo em{" "}
              <strong>{user.supportSession?.supportCompanyName || user.company?.name || "empresa"}</strong>. Sessão iniciada
              por <strong>{user.supportSession.impersonatedByEmail}</strong>.
            </Typography>
            <Button
              color="primary"
              variant="outlined"
              size="small"
              onClick={stopSupportSession}
            >
              Encerrar modo suporte
            </Button>
          </div>
        ) : null}
        {children ? children : null}
        </div>
      </main>
    </div>
  );
};

export default LoggedInLayout;

